<script setup>
import { computed, ref } from 'vue'
import { g1 } from '@/locales/i18n'
import { useLinks } from '@/composables/linksMix'
import { defaultDashboardLinks } from '@/store/userSettings/settingsDefaults'
import DialogTemplate from '@/components/Dialogs/DialogTemplate.vue'
import DragDropPicker from '@/components/DragDropGrid/DragDropPicker.vue'
import NavCard from '@/components/NavCard/NavCard.vue'
import { useStore } from 'vuex'
import { usePromotions } from '@/composables/usePromotions'

const store = useStore()
const { linksAll } = useLinks()
const { userHasRbwizardPromo } = usePromotions()

const options = ref([])
const l_selected = ref([])

const l_availableLinks = computed(() => {
  let res = linksAll.value
  res = {
    StudyCustomSolutions: res.StudyCustomSolutions,
    StudySolutions: res.StudySolutions,
    StudySolLibSolutions: res.StudySolLibSolutions,
    StudyReports: res.StudyReports,
    StudyCustomReports: res.StudyCustomReports,
    Coaching: res.Coaching,
    StudyPlansDashboard: res.StudyPlansDashboard,
    PracticeTrainer: res.PracticeTrainer,
    PracticeRangeBuilder: res.PracticeRangeBuilder,
    PracticeDrills: res.PracticeDrills,
    PracticeHands: res.PracticeHands,
    PracticeSessions: res.PracticeSessions,
    PracticeStats: res.PracticeStats,
    AnalyzeFilesV4: res.AnalyzeFilesV4,
    AnalyzeHandsV3: res.AnalyzeHandsV3,
    UserAppearance: res.UserAppearance,
    external_HelpCenter: res.external_HelpCenter,
  }
  Object.keys(res).forEach(key => {
    if (res[key].g1_hide && g1) {
      delete res[key]
    }
  })

  if (userHasRbwizardPromo.value) {
    res.external_Deals = linksAll.value.external_Deals
  }

  return res
})

const canConfirm = computed(() => l_selected.value.length >= 3)

const handleUpdate = value => {
  l_selected.value = value
}

const close = () => {
  store.dispatch('router_rm_dialog', 'dashboard-settings-dialog')
}

const updateSettings = patchData => {
  store.dispatch('setSettingsValue', { key: 'dashboardLinks', patchData })
  close()
}

const apply = () => {
  if (!canConfirm.value) return
  updateSettings(l_selected.value.map(i => i.id))
}

const resetToDefault = () => {
  let defaultLinks = defaultDashboardLinks()
  if (!userHasRbwizardPromo.value) {
    defaultLinks = defaultLinks.filter(i => i !== 'external_Deals')
  }

  updateSettings(defaultLinks)
}

const links = store.getters.dashboardLinks
const selected = []
Object.keys(l_availableLinks.value).forEach(key => {
  const item = {
    ...l_availableLinks.value[key],
    id: l_availableLinks.value[key].id || l_availableLinks.value[key].pathName,
  }
  options.value = [...options.value, item]
})
links.forEach(key => {
  const selectedItem = options.value.find(i => i.id === key)
  if (selectedItem) {
    selected.push(selectedItem)
  }
})
l_selected.value = selected.filter(i => i)
l_selected.value = l_selected.value.map(i => ({ ...i, id: i.id || i.pathName }))
</script>

<template>
  <DialogTemplate
    style="max-width: 72rem"
    class="dashsettings"
  >
    <div class="dashsettings_linkspicker f-column mh-0">
      <DragDropPicker
        class="tblcolpicker"
        :selected="l_selected"
        :options="options"
        style="--icon-after-radius: 50%"
        @update:selected="handleUpdate"
      >
        <template #selected_title>
          <div class="text-normal clr-white weight_600">
            {{ $t('dashboard_links.selected_links') }}
          </div>
          <div
            class="text-small clr-grey"
            v-html="
              $t(`dashboard_links.selected_links_sub`, {
                icon: `<span class='mdi mdi-trash-can-outline drgpicker_texticon drgpicker_texticon_encircled'></span>`,
                icon_drag: `<span class='mdi mdi-drag drgpicker_texticon drgpicker_texticon_encircled'></span>`,
              })
            "
          ></div>
        </template>
        <template #notselected_title>
          <div class="text-normal clr-white weight_600">
            {{ $t('dashboard_links.selected_links') }}
          </div>
          <div
            class="text-small clr-grey"
            v-html="
              $t(`dashboard_links.selected_links_sub`, {
                icon: `<span class='mdi mdi-trash-can-outline drgpicker_texticon drgpicker_texticon_encircled'></span>`,
                icon_drag: `<span class='mdi mdi-drag drgpicker_texticon drgpicker_texticon_encircled'></span>`,
              })
            "
          ></div>
        </template>
        <template #selected_item="{ item }">
          <NavCard
            :item="item"
            class="navcard_dashboard_small w-100 no-events"
            :style="`
              ${item.style};
               --clr-icon:${item.clr_main};
           `"
            :hasSub="false"
            :hasTag="false"
          />
        </template>
        <template #notselected_item="{ item }">
          <NavCard
            :item="item"
            class="navcard_dashboard_small w-100 no-events"
            :style="`
           ${item.style};
           --clr-icon:${item.clr_main};
           `"
            :hasSub="false"
            :hasTag="false"
          />
        </template>
        <template #shallowitem="{ item }">
          <NavCard
            v-if="item"
            :item="item"
            class="navcard_dashboard_small w-100 no-events"
            :style="`
          ${item.style};
           --clr-icon:${item.clr_main};
           `"
            :hasSub="false"
            :hasTag="false"
          />
        </template>
      </DragDropPicker>
    </div>
    <template #dialog_actions>
      <v-btn
        variant="text"
        @click="resetToDefault"
      >
        <i class="mr-2 mdi mdi-replay text-semi-large"></i>
        {{ $t('btn.reset_default') }}
      </v-btn>
      <v-btn
        id="trainer-settings-btn-close"
        variant="text"
        class="ml-auto mr-2"
        @click="close"
      >
        {{ $t('btn.close') }}
      </v-btn>

      <div
        v-if="!canConfirm"
        class="csst mt-auto"
      >
        <v-btn
          :disabled="true"
          color="primary"
        >
          {{ $t('btn.confirm') }}
        </v-btn>
        <div
          class="csst_content text-nowrap"
          style="top: -2rem; right: 0"
        >
          {{ $t('messages.dashboard_links_minimum', { num: 3 }) }}
        </div>
      </div>
      <v-btn
        v-else
        color="primary"
        :disabled="!canConfirm"
        @click="apply"
      >
        {{ $t('btn.confirm') }}
      </v-btn>
    </template>
  </DialogTemplate>
</template>

<style lang="scss" scoped>
.dashsettings {
  &_linkspicker {
    flex: 1;
    // padding: 0.5rem;
    overflow: hidden;
  }
}
</style>
